import { toast } from '@/hooks/use-toast';

// Error types for better categorization
export enum ErrorType {
  NETWORK = 'network',
  VALIDATION = 'validation',
  PERMISSION = 'permission',
  NOT_FOUND = 'not_found',
  SERVER = 'server',
  TIMEOUT = 'timeout',
  UNKNOWN = 'unknown'
}

// Error severity levels
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

// Enhanced error interface
export interface AppError {
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  originalError?: any;
  context?: Record<string, any>;
  timestamp: Date;
  retryable: boolean;
  userMessage: string;
}

// Retry configuration
export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  retryableErrors: ErrorType[];
}

// Default retry configuration
const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
  retryableErrors: [ErrorType.NETWORK, ErrorType.TIMEOUT, ErrorType.SERVER]
};

// Error classification function
export function classifyError(error: any): AppError {
  const timestamp = new Date();
  
  // Network errors
  if (!navigator.onLine) {
    return {
      type: ErrorType.NETWORK,
      severity: ErrorSeverity.HIGH,
      message: 'No internet connection',
      originalError: error,
      timestamp,
      retryable: true,
      userMessage: 'Please check your internet connection and try again.'
    };
  }

  // HTTP errors
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    switch (status) {
      case 400:
        return {
          type: ErrorType.VALIDATION,
          severity: ErrorSeverity.MEDIUM,
          message: data?.message || 'Invalid request data',
          originalError: error,
          timestamp,
          retryable: false,
          userMessage: data?.message || 'Please check your input and try again.'
        };

      case 401:
        return {
          type: ErrorType.PERMISSION,
          severity: ErrorSeverity.HIGH,
          message: 'Authentication required',
          originalError: error,
          timestamp,
          retryable: false,
          userMessage: 'Please log in to continue.'
        };

      case 403:
        return {
          type: ErrorType.PERMISSION,
          severity: ErrorSeverity.HIGH,
          message: 'Access denied',
          originalError: error,
          timestamp,
          retryable: false,
          userMessage: 'You don\'t have permission to perform this action.'
        };

      case 404:
        return {
          type: ErrorType.NOT_FOUND,
          severity: ErrorSeverity.MEDIUM,
          message: 'Resource not found',
          originalError: error,
          timestamp,
          retryable: false,
          userMessage: 'The requested resource was not found.'
        };

      case 408:
        return {
          type: ErrorType.TIMEOUT,
          severity: ErrorSeverity.MEDIUM,
          message: 'Request timeout',
          originalError: error,
          timestamp,
          retryable: true,
          userMessage: 'The request took too long. Please try again.'
        };

      case 429:
        return {
          type: ErrorType.SERVER,
          severity: ErrorSeverity.MEDIUM,
          message: 'Rate limit exceeded',
          originalError: error,
          timestamp,
          retryable: true,
          userMessage: 'Too many requests. Please wait a moment and try again.'
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          type: ErrorType.SERVER,
          severity: ErrorSeverity.HIGH,
          message: 'Server error',
          originalError: error,
          timestamp,
          retryable: true,
          userMessage: 'Server error occurred. Please try again later.'
        };

      default:
        return {
          type: ErrorType.UNKNOWN,
          severity: ErrorSeverity.MEDIUM,
          message: data?.message || `HTTP ${status} error`,
          originalError: error,
          timestamp,
          retryable: status >= 500,
          userMessage: data?.message || 'An unexpected error occurred. Please try again.'
        };
    }
  }

  // Request timeout
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return {
      type: ErrorType.TIMEOUT,
      severity: ErrorSeverity.MEDIUM,
      message: 'Request timeout',
      originalError: error,
      timestamp,
      retryable: true,
      userMessage: 'The request took too long. Please try again.'
    };
  }

  // Network connection errors
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
    return {
      type: ErrorType.NETWORK,
      severity: ErrorSeverity.HIGH,
      message: 'Network connection error',
      originalError: error,
      timestamp,
      retryable: true,
      userMessage: 'Network connection failed. Please check your connection and try again.'
    };
  }

  // Default unknown error
  return {
    type: ErrorType.UNKNOWN,
    severity: ErrorSeverity.MEDIUM,
    message: error.message || 'Unknown error occurred',
    originalError: error,
    timestamp,
    retryable: false,
    userMessage: error.message || 'An unexpected error occurred. Please try again.'
  };
}

// Enhanced error handler with retry logic
export class ErrorHandler {
  private static instance: ErrorHandler;
  private retryConfig: RetryConfig;

  private constructor(config?: Partial<RetryConfig>) {
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  }

  public static getInstance(config?: Partial<RetryConfig>): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler(config);
    }
    return ErrorHandler.instance;
  }

  // Handle error with automatic retry
  public async handleWithRetry<T>(
    operation: () => Promise<T>,
    context?: Record<string, any>,
    customConfig?: Partial<RetryConfig>
  ): Promise<T> {
    const config = { ...this.retryConfig, ...customConfig };
    let lastError: AppError;

    for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = classifyError(error);
        lastError.context = { ...lastError.context, ...context, attempt };

        // Log error for debugging
        console.error(`Attempt ${attempt}/${config.maxAttempts} failed:`, lastError);

        // Don't retry if error is not retryable or max attempts reached
        if (!config.retryableErrors.includes(lastError.type) || attempt === config.maxAttempts) {
          this.handleError(lastError);
          throw lastError;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1),
          config.maxDelay
        );

        // Add jitter to prevent thundering herd
        const jitteredDelay = delay + Math.random() * 1000;

        await new Promise(resolve => setTimeout(resolve, jitteredDelay));
      }
    }

    // This should never be reached, but TypeScript requires it
    throw lastError!;
  }

  // Handle error without retry
  public handleError(error: any, context?: Record<string, any>): AppError {
    const appError = error instanceof Error ? classifyError(error) : error as AppError;
    
    if (context) {
      appError.context = { ...appError.context, ...context };
    }

    // Log error for debugging
    console.error('Error handled:', appError);

    // Show user-friendly toast notification
    this.showErrorToast(appError);

    // Report critical errors (in production, this could send to error tracking service)
    if (appError.severity === ErrorSeverity.CRITICAL) {
      this.reportCriticalError(appError);
    }

    return appError;
  }

  // Show appropriate toast notification
  private showErrorToast(error: AppError): void {
    const variant = error.severity === ErrorSeverity.HIGH || error.severity === ErrorSeverity.CRITICAL 
      ? 'destructive' 
      : 'default';

    toast({
      title: this.getErrorTitle(error.type),
      description: error.userMessage,
      variant: variant as any,
      duration: error.severity === ErrorSeverity.CRITICAL ? 0 : 5000, // Critical errors don't auto-dismiss
    });
  }

  // Get user-friendly error title
  private getErrorTitle(type: ErrorType): string {
    switch (type) {
      case ErrorType.NETWORK:
        return 'Connection Error';
      case ErrorType.VALIDATION:
        return 'Validation Error';
      case ErrorType.PERMISSION:
        return 'Permission Denied';
      case ErrorType.NOT_FOUND:
        return 'Not Found';
      case ErrorType.SERVER:
        return 'Server Error';
      case ErrorType.TIMEOUT:
        return 'Request Timeout';
      default:
        return 'Error';
    }
  }

  // Report critical errors (placeholder for error tracking integration)
  private reportCriticalError(error: AppError): void {
    // In production, this would send to error tracking service like Sentry
    console.error('CRITICAL ERROR:', error);
    
    // Could also trigger additional notifications or alerts
    if (error.type === ErrorType.NETWORK && error.severity === ErrorSeverity.CRITICAL) {
      // Handle offline state
      this.handleOfflineState();
    }
  }

  // Handle offline state
  private handleOfflineState(): void {
    toast({
      title: 'You\'re Offline',
      description: 'Some features may not work properly. Please check your connection.',
      variant: 'destructive',
      duration: 0, // Don't auto-dismiss
    });
  }
}

// Convenience functions for common use cases
export const errorHandler = ErrorHandler.getInstance();

export async function withRetry<T>(
  operation: () => Promise<T>,
  context?: Record<string, any>,
  config?: Partial<RetryConfig>
): Promise<T> {
  return errorHandler.handleWithRetry(operation, context, config);
}

export function handleError(error: any, context?: Record<string, any>): AppError {
  return errorHandler.handleError(error, context);
}

// Loading state manager
export class LoadingStateManager {
  private static loadingStates = new Map<string, boolean>();
  private static listeners = new Map<string, Set<(loading: boolean) => void>>();

  public static setLoading(key: string, loading: boolean): void {
    this.loadingStates.set(key, loading);
    const listeners = this.listeners.get(key);
    if (listeners) {
      listeners.forEach(listener => listener(loading));
    }
  }

  public static getLoading(key: string): boolean {
    return this.loadingStates.get(key) || false;
  }

  public static subscribe(key: string, listener: (loading: boolean) => void): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(listener);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(key);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.listeners.delete(key);
        }
      }
    };
  }
}

// Hook for loading states
export function useLoadingState(key: string): [boolean, (loading: boolean) => void] {
  const [loading, setLoading] = React.useState(LoadingStateManager.getLoading(key));

  React.useEffect(() => {
    const unsubscribe = LoadingStateManager.subscribe(key, setLoading);
    return unsubscribe;
  }, [key]);

  const setLoadingState = React.useCallback((loading: boolean) => {
    LoadingStateManager.setLoading(key, loading);
  }, [key]);

  return [loading, setLoadingState];
}

// React import for hooks
import React from 'react';

// Offline detection hook
export function useOfflineDetection(): boolean {
  const [isOffline, setIsOffline] = React.useState(!navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      toast({
        title: 'Back Online',
        description: 'Your connection has been restored.',
        variant: 'default',
      });
    };

    const handleOffline = () => {
      setIsOffline(true);
      toast({
        title: 'You\'re Offline',
        description: 'Please check your internet connection.',
        variant: 'destructive',
        duration: 0,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOffline;
}

// API wrapper with enhanced error handling
export async function apiCall<T>(
  operation: () => Promise<T>,
  operationName: string,
  showSuccessToast = false,
  successMessage?: string
): Promise<T> {
  try {
    const result = await withRetry(operation, { operationName });
    
    if (showSuccessToast) {
      toast({
        title: 'Success',
        description: successMessage || `${operationName} completed successfully`,
        variant: 'default',
      });
    }
    
    return result;
  } catch (error) {
    handleError(error, { operationName });
    throw error;
  }
} 